import type {
  LeadFlowContractIssuesInput,
  LeadFlowContractRuleIssue,
} from "./types";

import { LEAD_FORM_CONTRACTS } from "./form-contracts";
import { getLeadFlowQuestionFieldNames } from "./get-lead-flow-question-field-names";
import { getRequiredLeadFormFieldNames } from "./get-required-lead-form-field-names";
import { POSTAL_CODE_FIELD_NAME } from "./lead-form-field-names";

/**
 * Checks a lead flow's questions against its form type's contract: each
 * contract field is asked at most once, an office-local flow never asks the
 * postal code its office supplies, and every required field is asked.
 * Coverage comes from the question types, so it needs no authored field
 * names. Studio reports these on publish and the page re-checks the content
 * it receives, so both share this one rule set.
 */
function getLeadFlowContractIssues({
  deliveryContext,
  formType,
  steps,
}: LeadFlowContractIssuesInput): LeadFlowContractRuleIssue[] {
  const issues: LeadFlowContractRuleIssue[] = [];
  const firstStepIndexByField = new Map<string, number>();
  const isPostalCodeInjected =
    LEAD_FORM_CONTRACTS[formType].postalSource[deliveryContext] ===
    "runtimeInjected";

  steps.forEach((step, stepIndex) => {
    // A step listing a field twice is reported by the step's own validation.
    const fieldNames = new Set(
      (step?.items ?? []).flatMap(getLeadFlowQuestionFieldNames),
    );

    for (const fieldName of fieldNames) {
      const firstStepIndex = firstStepIndexByField.get(fieldName);
      if (firstStepIndex === undefined) {
        firstStepIndexByField.set(fieldName, stepIndex);
      } else {
        issues.push({
          code: "duplicateField",
          fieldName,
          firstStepIndex,
          stepIndex,
        });
      }

      if (fieldName === POSTAL_CODE_FIELD_NAME && isPostalCodeInjected) {
        issues.push({ code: "injectedPostalCode", stepIndex });
      }
    }
  });

  if (steps.every(Boolean)) {
    for (const fieldName of getRequiredLeadFormFieldNames({
      deliveryContext,
      formType,
    })) {
      if (!firstStepIndexByField.has(fieldName)) {
        issues.push({ code: "missingField", fieldName });
      }
    }
  }

  return issues;
}

export { getLeadFlowContractIssues };
